import { useEffect, useState, Fragment } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Server,
  CheckCircle,
  XCircle,
  ChevronRight,
  AlertTriangle,
  Database,
} from "lucide-react";
import { StatusBadge } from "../../components/StatusBadge";
import { Dropdown } from "../../components/Dropdown";
import type {
  AssetFinding,
  AssetInventoryItem,
  AssetsData,
  SSPOption,
  TopAsset,
} from "../../types/dashboard";
import { DASHBOARD_URL } from "../../../constants/index";
import { getAPI } from "../../../api/frameworksApi";
import { listSSPs, type ApiEnvelope, type SspSummaryResponse } from "../../../api/frameworksApi";

type AssetComplianceStatus = "COMPLIANT" | "NON_COMPLIANT" | "NO_DATA";

interface DashboardEnvelope<T> {
  status: string;
  apiVersion: string;
  requestId: string;
  timestamp: string;
  data: T;
  pagination: unknown;
  errors: unknown;
}

interface AssetApiItem {
  inventoryItemId: string;
  description: string;
  componentType: string;
  componentTitle: string;
  systemName: string;
  compliancePercent: number | null;
  satisfiedCount: number;
  otherThanSatisfiedCount: number;
  totalControls: number;
  complianceStatus: AssetComplianceStatus;
}

interface AssetsApiData {
  assets: AssetApiItem[];
  topNonCompliant: AssetApiItem[];
  topCompliant: AssetApiItem[];
  totalAssets: number;
  compliantAssets: number;
  nonCompliantAssets: number;
}

interface AssetFindingApiItem {
  findingId: string;
  controlId: string;
  title: string;
  description: string;
  targetStatus: "pass" | "fail";
  implementationStatus: string;
}

function normalizeAssetStatus(
  complianceStatus: AssetComplianceStatus,
): AssetInventoryItem["status"] {
  if (complianceStatus === "COMPLIANT") return "compliant";
  if (complianceStatus === "NON_COMPLIANT") return "non-compliant";
  return "no-data";
}

function normalizeAsset(asset: AssetApiItem): AssetInventoryItem {
  return {
    inventoryItemId: asset.inventoryItemId,
    description: asset.description,
    componentType: asset.componentType,
    componentTitle: asset.componentTitle,
    systemName: asset.systemName,
    compliancePercent: asset.compliancePercent,
    status: normalizeAssetStatus(asset.complianceStatus),
    satisfiedCount: asset.satisfiedCount ?? 0,
    otherThanSatisfiedCount: asset.otherThanSatisfiedCount ?? 0,
    totalControls: asset.totalControls ?? 0,
  };
}

function normalizeTopAsset(asset: AssetApiItem): TopAsset {
  return {
    inventoryItemId: asset.inventoryItemId,
    description: asset.description,
    componentType: asset.componentType,
    compliancePercent: asset.compliancePercent ?? 0,
    otherThanSatisfiedCount: asset.otherThanSatisfiedCount ?? 0,
    componentTitle: asset.componentTitle,
  };
}

function normalizeAssetsData(data: AssetsApiData): AssetsData {
  return {
    totalAssets: data.totalAssets ?? 0,
    compliantAssets: data.compliantAssets ?? 0,
    nonCompliantAssets: data.nonCompliantAssets ?? 0,
    topNonCompliant: (data.topNonCompliant ?? []).map(normalizeTopAsset),
    topCompliant: (data.topCompliant ?? []).map(normalizeTopAsset),
    assets: (data.assets ?? []).map(normalizeAsset),
  };
}

function normalizeFinding(finding: AssetFindingApiItem): AssetFinding {
  return {
    findingId: finding.findingId,
    controlId: finding.controlId,
    title: finding.title,
    description: finding.description,
    status: finding.targetStatus,
    implementationStatus: finding.implementationStatus,
  };
}

function formatImplementationStatus(value: string) {
  return value
    .split(/[_-]/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function ComplianceBar({ percent, size = "md" }: { percent: number | null; size?: "sm" | "md" }) {
  if (percent === null) {
    return <span className="text-[10px] text-[#94a3b8] dark:text-white/30">No Data</span>;
  }
  const color =
    percent >= 90 ? "bg-emerald-500" : percent >= 70 ? "bg-amber-500" : "bg-red-500";
  const h = size === "sm" ? "h-1.5" : "h-2";
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className={`flex-1 ${h} bg-[#f1f5f9] dark:bg-white/[0.06] rounded-full overflow-hidden`}>
        <motion.div
          className={`h-full rounded-full ${color}`}
          initial={{ width: 0 }}
          animate={{ width: `${Math.max(0, Math.min(percent, 100))}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        />
      </div>
      <span className="text-[11px] font-bold text-[#0f172a] dark:text-white/70 w-12 text-right">
        {percent.toFixed(2).replace(/\.00$/, "")}%
      </span>
    </div>
  );
}

function StatusPill({ status }: { status: "compliant" | "non-compliant" | "no-data" }) {
  const styles = {
    compliant: "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400",
    "non-compliant": "bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400",
    "no-data": "bg-slate-100 dark:bg-white/10 text-slate-500 dark:text-white/40",
  };
  const labels = { compliant: "Compliant", "non-compliant": "Non-Compliant", "no-data": "No Data" };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

const accentMap: Record<string, { icon: string; ring: string }> = {
  indigo: {
    icon: "text-indigo-600 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-500/15",
    ring: "border-l-indigo-500",
  },
  emerald: {
    icon: "text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-500/15",
    ring: "border-l-emerald-500",
  },
  red: {
    icon: "text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-500/15",
    ring: "border-l-red-500",
  },
};

function DashboardPanelState({
  title,
  body,
  tone = "neutral",
}: {
  title: string;
  body: string;
  tone?: "neutral" | "error";
}) {
  const isError = tone === "error";
  return (
    <div className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320] px-6 py-10 text-center">
      <div
        className={`mx-auto mb-3 flex size-10 items-center justify-center rounded-full ${
          isError
            ? "bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-400"
            : "bg-slate-100 text-slate-500 dark:bg-white/[0.06] dark:text-white/40"
        }`}
      >
        <Database className="size-4" />
      </div>
      <p className="text-sm font-bold text-[#0f172a] dark:text-white">{title}</p>
      <p className="mt-1 text-xs text-[#64748b] dark:text-white/40">{body}</p>
    </div>
  );
}

export function MyAssetsTab() {
  const [sspOptions, setSspOptions] = useState<SSPOption[]>([]);
  const [selectedSSP, setSelectedSSP] = useState("");
  const [assetsData, setAssetsData] = useState<AssetsData | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [findingsByAsset, setFindingsByAsset] = useState<Record<string, AssetFinding[]>>({});
  const [loadingFindingsId, setLoadingFindingsId] = useState<string | null>(null);
  const [findingsErrorByAsset, setFindingsErrorByAsset] = useState<Record<string, string>>({});
  const [sspsLoading, setSspsLoading] = useState(true);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadSsps = async () => {
      setSspsLoading(true);
      setPageError(null);
      try {
        const response: ApiEnvelope<SspSummaryResponse[]> = await listSSPs();
        if (cancelled) return;
        const options = (response.data ?? []).map((ssp) => ({
          id: ssp.id,
          title: ssp.title?.trim() || ssp.documentId || ssp.id,
        }));
        setSspOptions(options);
        setSelectedSSP((current) => current || options[0]?.id || "");
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load SSPs", error);
        setPageError("Unable to load System Security Plans right now.");
      } finally {
        if (!cancelled) {
          setSspsLoading(false);
        }
      }
    };

    void loadSsps();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedSSP) return;

    let cancelled = false;

    const loadAssets = async () => {
      setAssetsLoading(true);
      setPageError(null);
      setExpandedRow(null);
      setLoadingFindingsId(null);
      setFindingsByAsset({});
      setFindingsErrorByAsset({});
      try {
        const response = await getAPI(
          DASHBOARD_URL.assets(selectedSSP),
        ) as DashboardEnvelope<AssetsApiData>;
        if (cancelled) return;
        setAssetsData(normalizeAssetsData(response.data));
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load dashboard assets", error);
        setAssetsData(null);
        setPageError("Unable to load asset inventory for the selected SSP.");
      } finally {
        if (!cancelled) {
          setAssetsLoading(false);
        }
      }
    };

    void loadAssets();

    return () => {
      cancelled = true;
    };
  }, [selectedSSP]);

  const handleExpand = async (inventoryItemId: string) => {
    const nextExpanded = expandedRow === inventoryItemId ? null : inventoryItemId;
    setExpandedRow(nextExpanded);

    if (!nextExpanded || findingsByAsset[inventoryItemId] || findingsErrorByAsset[inventoryItemId]) {
      return;
    }

    setLoadingFindingsId(inventoryItemId);
    try {
      const response = await getAPI(
        DASHBOARD_URL.assetFindings(inventoryItemId, selectedSSP),
      ) as DashboardEnvelope<AssetFindingApiItem[]>;
      setFindingsByAsset((current) => ({
        ...current,
        [inventoryItemId]: (response.data ?? []).map(normalizeFinding),
      }));
    } catch (error) {
      console.error(`Failed to load findings for asset ${inventoryItemId}`, error);
      setFindingsErrorByAsset((current) => ({
        ...current,
        [inventoryItemId]: "Unable to load findings for this asset.",
      }));
    } finally {
      setLoadingFindingsId((current) => (current === inventoryItemId ? null : current));
    }
  };

  const data = assetsData;

  const kpis = [
    { label: "Total Assets", value: data?.totalAssets ?? 0, icon: Server, accent: "indigo" },
    { label: "Compliant", value: data?.compliantAssets ?? 0, icon: CheckCircle, accent: "emerald" },
    { label: "Non-Compliant", value: data?.nonCompliantAssets ?? 0, icon: XCircle, accent: "red" },
  ];

  if (sspsLoading) {
    return (
      <DashboardPanelState
        title="Loading dashboard assets"
        body="Fetching System Security Plans and asset inventory."
      />
    );
  }

  if (pageError && !selectedSSP) {
    return (
      <DashboardPanelState
        title="Dashboard unavailable"
        body={pageError}
        tone="error"
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <label className="text-xs font-bold text-[#0f172a] dark:text-white/70 whitespace-nowrap">
          System Security Plan:
        </label>
        <Dropdown
          size="sm"
          variant="pill"
          className="min-w-[260px]"
          disabled={sspOptions.length === 0}
          value={selectedSSP}
          onChange={(v) => setSelectedSSP(v)}
          placeholder={sspOptions.length === 0 ? "No SSPs available" : undefined}
          options={
            sspOptions.length === 0
              ? [{ value: "", label: "No SSPs available" }]
              : sspOptions.map((ssp) => ({ value: ssp.id, label: ssp.title }))
          }
        />
      </div>

      {pageError && selectedSSP && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
          {pageError}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        {kpis.map((kpi, i) => {
          const Icon = kpi.icon;
          const ac = accentMap[kpi.accent];
          return (
            <motion.div
              key={kpi.label}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07, duration: 0.35 }}
              className={`bg-white dark:bg-[#131320] rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] border-l-[3px] ${ac.ring} p-4 hover:shadow-md dark:hover:shadow-black/30 transition-all hover:-translate-y-px`}
            >
              <div className="flex items-center gap-3">
                <div className={`size-9 rounded-lg flex items-center justify-center ${ac.icon}`}>
                  <Icon className="size-4" />
                </div>
                <div>
                  <p className="text-2xl font-black text-[#0f172a] dark:text-white tracking-tight leading-none">
                    {assetsLoading ? "" : kpi.value}
                  </p>
                  <p className="text-xs font-bold text-[#0f172a] dark:text-white/70 mt-0.5">
                    {kpi.label}
                  </p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white dark:bg-[#131320] rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] overflow-hidden"
        >
          <div className="flex items-center gap-2 px-5 py-3.5 border-b border-[#f1f5f9] dark:border-white/[0.04]">
            <AlertTriangle className="size-3.5 text-red-500" />
            <h3 className="text-sm font-bold text-[#0f172a] dark:text-white">
              Top 5 Non-Compliant
            </h3>
          </div>
          <div className="divide-y divide-[#f8f9fc] dark:divide-white/[0.03]">
            {(data?.topNonCompliant ?? []).length > 0 ? (
              (data?.topNonCompliant ?? []).map((asset, idx) => (
                <div
                  key={asset.inventoryItemId}
                  className="px-5 py-3 hover:bg-[#f8f9fc] dark:hover:bg-white/[0.02] transition-colors"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[10px] font-black text-red-500 bg-red-100 dark:bg-red-500/15 size-5 rounded flex items-center justify-center flex-shrink-0">
                        {idx + 1}
                      </span>
                      <span className="text-xs font-bold text-[#0f172a] dark:text-white/80 truncate">
                        {asset.description}
                      </span>
                    </div>
                    <span className="text-[10px] font-medium text-[#94a3b8] dark:text-white/30 flex-shrink-0 ml-2">
                      {asset.componentType}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <ComplianceBar percent={asset.compliancePercent} size="sm" />
                    <span className="text-[10px] text-red-500 font-bold ml-2">
                      {asset.otherThanSatisfiedCount} failed
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-5 py-8 text-center text-xs text-[#94a3b8] dark:text-white/30">
                {assetsLoading ? "Loading non-compliant assets" : "No non-compliant assets found."}
              </div>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-[#131320] rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] overflow-hidden"
        >
          <div className="flex items-center gap-2 px-5 py-3.5 border-b border-[#f1f5f9] dark:border-white/[0.04]">
            <CheckCircle className="size-3.5 text-emerald-500" />
            <h3 className="text-sm font-bold text-[#0f172a] dark:text-white">
              Top 5 Compliant
            </h3>
          </div>
          <div className="divide-y divide-[#f8f9fc] dark:divide-white/[0.03]">
            {(data?.topCompliant ?? []).length > 0 ? (
              (data?.topCompliant ?? []).map((asset) => (
                <div
                  key={asset.inventoryItemId}
                  className="px-5 py-3 hover:bg-[#f8f9fc] dark:hover:bg-white/[0.02] transition-colors"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[10px] font-black text-emerald-600 bg-emerald-100 dark:bg-emerald-500/15 size-5 rounded flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="size-3" />
                      </span>
                      <span className="text-xs font-bold text-[#0f172a] dark:text-white/80 truncate">
                        {asset.description}
                      </span>
                    </div>
                    <span className="text-[10px] font-medium text-[#94a3b8] dark:text-white/30 flex-shrink-0 ml-2">
                      {asset.componentType}
                    </span>
                  </div>
                  <ComplianceBar percent={asset.compliancePercent} size="sm" />
                </div>
              ))
            ) : (
              <div className="px-5 py-8 text-center text-xs text-[#94a3b8] dark:text-white/30">
                {assetsLoading ? "Loading compliant assets" : "No compliant assets found."}
              </div>
            )}
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white dark:bg-[#131320] rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-[#f1f5f9] dark:border-white/[0.04]">
          <h3 className="text-sm font-bold text-[#0f172a] dark:text-white">
            Asset Inventory
          </h3>
          <p className="text-[10px] text-[#94a3b8] dark:text-white/30 mt-0.5">
            Click a row to view findings
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[#f1f5f9] dark:border-white/[0.04]">
                <th className="px-5 py-2.5 w-8" />
                {["Asset Name", "Type", "Component", "System", "Compliance", "Status", "Failed"].map(
                  (h) => (
                    <th
                      key={h}
                      className="text-center px-3 py-2.5 text-[10px] font-bold tracking-wide text-[#94a3b8] dark:text-white/30"
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {assetsLoading ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-5 py-10 text-center text-xs text-[#94a3b8] dark:text-white/30"
                  >
                    Loading asset inventory
                  </td>
                </tr>
              ) : (data?.assets ?? []).length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-5 py-10 text-center text-xs text-[#94a3b8] dark:text-white/30"
                  >
                    No assets found for the selected System Security Plan.
                  </td>
                </tr>
              ) : (
                (data?.assets ?? []).map((asset) => {
                  const isExpanded = expandedRow === asset.inventoryItemId;
                  const assetFindings = findingsByAsset[asset.inventoryItemId] || [];
                  const findingsError = findingsErrorByAsset[asset.inventoryItemId];
                  const findingsLoading = loadingFindingsId === asset.inventoryItemId;

                  return (
                    <Fragment key={asset.inventoryItemId}>
                      <tr
                        onClick={() => void handleExpand(asset.inventoryItemId)}
                        className={`border-b border-[#f8f9fc] dark:border-white/[0.03] cursor-pointer transition-colors ${
                          isExpanded
                            ? "bg-[#f8f9fc] dark:bg-white/[0.03]"
                            : "hover:bg-[#f8f9fc] dark:hover:bg-white/[0.02]"
                        }`}
                      >
                        <td className="px-5 py-2.5">
                          <ChevronRight
                            className={`size-3.5 text-[#94a3b8] dark:text-white/30 transition-transform duration-200 ${
                              isExpanded ? "rotate-90" : ""
                            }`}
                          />
                        </td>
                        <td className="px-3 py-2.5 font-medium text-[#0f172a] dark:text-white/80">
                          {asset.description}
                        </td>
                        <td className="px-3 py-2.5 text-[#64748b] dark:text-white/40">
                          {asset.componentType}
                        </td>
                        <td className="px-3 py-2.5 text-[#64748b] dark:text-white/40">
                          {asset.componentTitle}
                        </td>
                        <td className="px-3 py-2.5 text-[#64748b] dark:text-white/40">
                          {asset.systemName}
                        </td>
                        <td className="px-3 py-2.5">
                          <ComplianceBar percent={asset.compliancePercent} size="sm" />
                        </td>
                        <td className="px-3 py-2.5">
                          <StatusPill status={asset.status} />
                        </td>
                        <td className="px-3 py-2.5 text-[#64748b] dark:text-white/40">
                          {asset.totalControls > 0
                            ? `${asset.otherThanSatisfiedCount} / ${asset.totalControls}`
                            : "—"}
                        </td>
                      </tr>
                      <AnimatePresence>
                        {isExpanded && (
                          <tr>
                            <td colSpan={8} className="p-0">
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.25 }}
                                className="overflow-hidden"
                              >
                                <div className="px-8 py-3 bg-[#f1f5f9]/50 dark:bg-white/[0.015]">
                                  {findingsLoading ? (
                                    <div className="text-center py-6 text-xs text-[#94a3b8] dark:text-white/30">
                                      Loading findings
                                    </div>
                                  ) : findingsError ? (
                                    <div className="text-center py-6">
                                      <AlertTriangle className="size-8 mx-auto text-red-500 mb-2" />
                                      <p className="text-xs text-red-600 dark:text-red-300">
                                        {findingsError}
                                      </p>
                                    </div>
                                  ) : assetFindings.length > 0 ? (
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr>
                                          {["Control ID", "Finding", "Description", "Status", "Implementation"].map(
                                            (h) => (
                                              <th
                                                key={h}
                                                className="text-center px-3 py-2 text-[10px] font-bold tracking-wide text-[#94a3b8] dark:text-white/30"
                                              >
                                                {h}
                                              </th>
                                            ),
                                          )}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {assetFindings.map((f) => (
                                          <tr
                                            key={f.findingId ?? `${f.controlId}-${f.title}`}
                                            className="border-t border-[#e2e8f0]/50 dark:border-white/[0.04]"
                                          >
                                            <td className="px-3 py-2 font-mono font-bold text-[#0f172a] dark:text-white/70">
                                              {f.controlId?.toUpperCase()}
                                            </td>
                                            <td className="px-3 py-2 font-medium text-[#0f172a] dark:text-white/80">
                                              {f.title}
                                            </td>
                                            <td className="px-3 py-2 text-[#64748b] dark:text-white/40 max-w-[300px] truncate">
                                              {f.description}
                                            </td>
                                            <td className="px-3 py-2">
                                              <StatusBadge status={f.status === "fail" ? "fail" : "pass"} />
                                            </td>
                                            <td className="px-3 py-2 text-[#64748b] dark:text-white/40">
                                              {formatImplementationStatus(f.implementationStatus)}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  ) : (
                                    <div className="text-center py-6">
                                      <Database className="size-8 mx-auto text-[#94a3b8] dark:text-white/20 mb-2" />
                                      <p className="text-xs text-[#94a3b8] dark:text-white/30">
                                        No findings available for this asset
                                      </p>
                                      <p className="text-[10px] text-[#94a3b8] dark:text-white/20 mt-0.5">
                                        This asset has no current control-level drilldown items.
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            </td>
                          </tr>
                        )}
                      </AnimatePresence>
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
}
